import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, Button } from '../../../components/ui';
import { Upload, FileText, Trash2, X, CheckCircle, Ticket, Plus, AlertCircle, Loader2, Phone, GitBranch, Search, User } from 'lucide-react';
import { ticketService } from '../../../api/ticketService';
import { fileService } from '../../../api/fileService';
import LocationSelector from '../../../components/location/LocationSelector';
import { useTicketRelationsEnabled } from '../../../contexts/AppConfigContext';
import toast from 'react-hot-toast';

/** HDO/agent intake channel — persisted on tickets.channel; source stays "agent". */
const HDO_INTAKE_CHANNELS = [
  { value: 'web', label: 'Web' },
  { value: 'phone', label: 'Phone' },
  { value: 'walk_in', label: 'Walk-in' },
];

const getIntakeChannelLabel = (channelValue) => {
  const match = HDO_INTAKE_CHANNELS.find((option) => option.value === channelValue);
  return match?.label ?? channelValue;
};

const CreateTicketForm = ({
  source = 'portal',
  redirectPath = '/portal/tickets',
  title = 'Create New Ticket',
  subtitle = 'Submit a support request to get help with your issue',
  phoneNumber: initialPhoneNumber = null
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // V1 parent-child "create child" path. Strict gating to keep customer portal
  // behavior 100% unchanged:
  //   1. URL must carry ?parentTicketId=NNN AND
  //   2. caller must be agent (`source === 'agent'`) AND
  //   3. backend feature flag `ticketRelations` must be on.
  // If any gate fails, the form behaves exactly as before — no banner, regular create API.
  const ticketRelationsEnabled = useTicketRelationsEnabled();
  const rawParentTicketId = searchParams.get('parentTicketId');
  const rawParentTicketNumber = searchParams.get('parentTicketNumber');
  const parentTicketIdNum = (() => {
    const n = Number(rawParentTicketId);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const isCreateChildMode = ticketRelationsEnabled
    && source === 'agent'
    && parentTicketIdNum !== null;
  const isAgentOnBehalfCreate = source === 'agent' && !isCreateChildMode;

  const [formData, setFormData] = useState({
    subject: '',
    inquiryTypeId: null,
    inquiryCategoryId: null,
    inquirySubCategoryId: null,
    inquiryPriorityLevelId: null,
    description: '',
    phoneNumber: '',
    attachments: []
  });
  // Portal-only display of the logged-in customer (read-only Name/Email).
  const [customerProfile, setCustomerProfile] = useState({ name: '', email: '' });
  // Agent on-behalf: search + selected customer requester
  const [customerEmailSearch, setCustomerEmailSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [customerSearchError, setCustomerSearchError] = useState('');
  /** Agent/HDO intake channel (tickets.channel); default preserves existing web behaviour. */
  const [intakeChannel, setIntakeChannel] = useState('web');
  const isPortalCreate = source !== 'agent';
  const [locationData, setLocationData] = useState({
    countryId: null,
    countyId: null,
    subCountyId: null,
    wardId: null
  });
  const [inquiryTypes, setInquiryTypes] = useState([]);
  const [inquiryCategories, setInquiryCategories] = useState([]);
  const [inquirySubCategories, setInquirySubCategories] = useState([]);
  const [loadingInquiryMasters, setLoadingInquiryMasters] = useState(false);
  const loadingReferences = loadingInquiryMasters;
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdTicketId, setCreatedTicketId] = useState(null);
  const [createdTicketNumber, setCreatedTicketNumber] = useState(null);
  /** Set when portal create auto-linked to a canonical ticket (informational only). */
  const [linkedDuplicateOfNumber, setLinkedDuplicateOfNumber] = useState(null);
  const [oversizedFiles, setOversizedFiles] = useState([]); // Track files that exceed size limit
  const fileInputRef = React.useRef(null);
  const submitInProgressRef = React.useRef(false);
  /** Section roots for scroll-to-first validation error on submit */
  const fieldScrollRefs = useRef({});

  // Template fields state
  const [templateFields, setTemplateFields] = useState([]);
  const [loadingTemplateFields, setLoadingTemplateFields] = useState(false);
  const [customFieldValues, setCustomFieldValues] = useState({}); // Store custom field values

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB per file

  // Load phone number from props, location state, or query params on mount.
  // For portal users, also surface Name/Email from the existing session userDetails
  // (no extra API) and fall back to session phone when nothing else was passed.
  useEffect(() => {
    // Priority: explicit prop -> location state -> query params
    const phoneFromProp = initialPhoneNumber;
    const phoneFromState = location.state?.phoneNumber;
    const phoneFromQuery = searchParams.get('phone_number') || searchParams.get('phone');
    const explicitPhone = phoneFromProp || phoneFromState || (phoneFromQuery ? decodeURIComponent(phoneFromQuery) : '');

    let sessionPhone = '';
    if (source !== 'agent') {
      try {
        const userDetailsStr = sessionStorage.getItem('userDetails');
        if (userDetailsStr) {
          const userDetails = JSON.parse(userDetailsStr);
          const name =
            userDetails?.fullName ||
            [userDetails?.firstName, userDetails?.lastName].filter(Boolean).join(' ') ||
            sessionStorage.getItem('userName') ||
            '';
          const email = userDetails?.email || '';
          setCustomerProfile({ name, email });
          if (userDetails?.phone) {
            sessionPhone = String(userDetails.phone);
          }
        } else {
          const fallbackName = sessionStorage.getItem('userName') || '';
          setCustomerProfile({ name: fallbackName, email: '' });
        }
      } catch (error) {
        console.error('Error parsing userDetails from sessionStorage:', error);
      }
    }

    const phoneSource = explicitPhone || (source !== 'agent' ? sessionPhone : '');
    if (phoneSource) {
      const numericPhone = String(phoneSource).replace(/\D/g, '').slice(0, 10);
      setFormData((prev) => ({
        ...prev,
        phoneNumber: numericPhone,
      }));
    }
  }, [initialPhoneNumber, location.state, searchParams, source]);

  const referenceToastShownRef = useRef(false);

  const loadIndependentInquiryMasters = useCallback(async () => {
    setLoadingInquiryMasters(true);
    try {
      const types = await ticketService.getInquiryTypesForCreate();
      setInquiryTypes(Array.isArray(types) ? types : []);
      if (!referenceToastShownRef.current) {
        referenceToastShownRef.current = true;
        if (!types?.length) {
          toast.error('No active inquiry types available. Please contact support.');
        }
      }
    } catch (error) {
      console.error('Error loading inquiry masters:', error);
      toast.error(error?.message || 'Failed to load inquiry masters');
      setInquiryTypes([]);
    } finally {
      setLoadingInquiryMasters(false);
    }
  }, []);

  useEffect(() => {
    loadIndependentInquiryMasters();
  }, [loadIndependentInquiryMasters]);

  useEffect(() => {
    let cancelled = false;
    const loadCategories = async () => {
      if (!formData.inquiryTypeId) {
        setInquiryCategories([]);
        return;
      }
      try {
        const rows = await ticketService.getInquiryCategoriesForCreate(formData.inquiryTypeId);
        if (!cancelled) setInquiryCategories(Array.isArray(rows) ? rows : []);
      } catch (error) {
        console.error('Error loading inquiry categories:', error);
        if (!cancelled) {
          setInquiryCategories([]);
          toast.error(error?.message || 'Failed to load inquiry categories');
        }
      }
    };
    loadCategories();
    return () => { cancelled = true; };
  }, [formData.inquiryTypeId]);

  useEffect(() => {
    let cancelled = false;
    const loadSubCategories = async () => {
      if (!formData.inquiryCategoryId) {
        setInquirySubCategories([]);
        return;
      }
      try {
        const rows = await ticketService.getInquirySubCategoriesForCreate(formData.inquiryCategoryId);
        if (!cancelled) setInquirySubCategories(Array.isArray(rows) ? rows : []);
      } catch (error) {
        console.error('Error loading inquiry sub categories:', error);
        if (!cancelled) {
          setInquirySubCategories([]);
          toast.error(error?.message || 'Failed to load inquiry sub categories');
        }
      }
    };
    loadSubCategories();
    return () => { cancelled = true; };
  }, [formData.inquiryCategoryId]);

  const computeValidationErrors = () => {
    const newErrors = {};

    if (isAgentOnBehalfCreate && !selectedCustomer?.userId) {
      newErrors.customerRequester = 'Please search and select a valid customer';
    }

    if (!formData.subject || formData.subject.trim().length < 5) {
      newErrors.subject = 'Subject must be at least 5 characters';
    }

    const descriptionTrimmed = formData.description?.trim() ?? '';
    if (!descriptionTrimmed) {
      newErrors.description = 'Description is required';
    } else if (descriptionTrimmed.length < 10) {
      newErrors.description = 'Description must be at least 10 characters.';
    }

    if (!formData.inquiryTypeId) {
      newErrors.inquiryType = 'Please select an inquiry type';
    }
    if (!formData.inquiryCategoryId) {
      newErrors.inquiryCategory = 'Please select an inquiry category';
    }
    if (!formData.inquirySubCategoryId) {
      newErrors.inquirySubCategory = 'Please select an inquiry sub category';
    }

    const hasOversizedFiles = oversizedFiles.length > 0 ||
      formData.attachments.some(file => file.size > MAX_FILE_SIZE);

    if (hasOversizedFiles) {
      newErrors.attachments = 'One or more files exceed the maximum size of 5MB. Please remove oversized files before submitting.';
    }

    return newErrors;
  };

  /** First error key in top-to-bottom form order (matches scroll targets). */
  const getFirstErrorScrollKey = (newErrors) => {
    const orderedKeys = [
      'customerRequester',
      'subject',
      'inquiryType',
      'inquiryCategory',
      'inquirySubCategory',
      'description',
      'attachments',
    ];
    for (const k of orderedKeys) {
      if (newErrors[k]) return k;
    }
    return Object.keys(newErrors)[0] ?? null;
  };

  const scrollToFirstErrorField = (key) => {
    if (!key) return;
    const el = fieldScrollRefs.current[key];
    if (!el || typeof el.scrollIntoView !== 'function') return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const focusable = el.querySelector('input:not([type="hidden"]):not([type="file"]), select, textarea');
    if (focusable && typeof focusable.focus === 'function') {
      window.setTimeout(() => {
        try {
          focusable.focus({ preventScroll: true });
        } catch {
          focusable.focus();
        }
      }, 400);
    }

  };

  const invalidateSelectedCustomer = () => {
    setSelectedCustomer(null);
    setFormData((prev) => ({ ...prev, phoneNumber: '' }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.customerRequester;
      return next;
    });
  };

  const handleCustomerEmailChange = (value) => {
    setCustomerEmailSearch(value);
    setCustomerSearchError('');
    if (selectedCustomer && value.trim().toLowerCase() !== String(selectedCustomer.email || '').trim().toLowerCase()) {
      invalidateSelectedCustomer();
    }
  };

  const handleSearchCustomer = async () => {
    const email = customerEmailSearch.trim();
    if (!email) {
      setCustomerSearchError('Please enter a customer email address');
      invalidateSelectedCustomer();
      return;
    }

    setCustomerSearchLoading(true);
    setCustomerSearchError('');
    invalidateSelectedCustomer();

    try {
      const response = await ticketService.lookupCustomerByEmail(email);
      const apiData = response?.data;
      if (apiData?.success && apiData?.data) {
        const customer = apiData.data;
        setSelectedCustomer(customer);
        const phoneDigits = String(customer.phone || '').replace(/\D/g, '').slice(0, 10);
        setFormData((prev) => ({
          ...prev,
          phoneNumber: phoneDigits
        }));
        setCustomerEmailSearch(customer.email || email);
        setErrors((prev) => {
          const next = { ...prev };
          delete next.customerRequester;
          return next;
        });
      } else {
        const message = apiData?.message || 'Customer not found. Please verify the email address.';
        setCustomerSearchError(message);
      }
    } catch (error) {
      const message = error?.message || 'Customer not found. Please verify the email address.';
      setCustomerSearchError(
        /not found|404|verify the email/i.test(String(message))
          ? 'Customer not found. Please verify the email address.'
          : message
      );
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const handleChangeCustomer = () => {
    setCustomerEmailSearch('');
    setCustomerSearchError('');
    invalidateSelectedCustomer();
  };

  const handleCustomerSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearchCustomer();
    }
  };

  const agentFormLocked = isAgentOnBehalfCreate && !selectedCustomer;

  // Phase 1: legacy category templates deferred — Inquiry masters drive create.
  useEffect(() => {
    setTemplateFields([]);
    setCustomFieldValues({});
  }, []);

  const handleInputChange = (field, value) => {
    // For phone number, only allow numeric input and limit to 10 digits
    if (field === 'phoneNumber') {
      const numericValue = value.replace(/\D/g, '');
      const limitedValue = numericValue.slice(0, 10);
      setFormData(prev => ({
        ...prev,
        [field]: limitedValue
      }));
    } else if (field === 'inquiryTypeId') {
      setFormData(prev => ({
        ...prev,
        inquiryTypeId: value,
        inquiryCategoryId: null,
        inquirySubCategoryId: null
      }));
      setInquiryCategories([]);
      setInquirySubCategories([]);
    } else if (field === 'inquiryCategoryId') {
      setFormData(prev => ({
        ...prev,
        inquiryCategoryId: value,
        inquirySubCategoryId: null
      }));
      setInquirySubCategories([]);
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleCustomFieldChange = (fieldCode, value) => {
    setCustomFieldValues(prev => ({
      ...prev,
      [fieldCode]: value
    }));

    // Clear error when user starts typing
    if (errors[`custom_${fieldCode}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`custom_${fieldCode}`];
        return newErrors;
      });
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newOversizedFiles = []; // 5MB limit
    const validFiles = [];

    files.forEach(file => {
      if (file.size > MAX_FILE_SIZE) {
        newOversizedFiles.push({
          name: file.name,
          size: file.size
        });
        toast.error(`File "${file.name}" exceeds the maximum size of 5MB. Please select a smaller file.`);
      } else {
        validFiles.push(file);
      }
    });

    // Update oversized files list
    setOversizedFiles(prev => [...prev, ...newOversizedFiles]);

    // Add only valid files to attachments
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...validFiles]
    }));

    // Clear error for attachments if all files are valid
    if (newOversizedFiles.length === 0 && validFiles.length > 0) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.attachments;
        return newErrors;
      });
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
    // Clear attachment errors if no attachments remain
    if (formData.attachments.length === 1) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.attachments;
        return newErrors;
      });
      setOversizedFiles([]);
    }
  };

  const removeOversizedFile = (index) => {
    setOversizedFiles(prev => prev.filter((_, i) => i !== index));
    // Clear attachment errors if no oversized files remain
    if (oversizedFiles.length === 1) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.attachments;
        return newErrors;
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const sanitizeTicketCreationError = (rawMessage) => {
    const fallback = 'Failed to create ticket. Please try again.';
    if (!rawMessage) return fallback;

    const message = String(rawMessage).replace(/^API Error:\s*\d+\s*/i, '').trim();
    if (!message) return fallback;

    const technicalPattern =
      /could not execute statement|duplicate key value|violates|constraint|sqlstate|org\.hibernate|jdbc|insert into|detail:\s*key|position:\s*\d+/i;

    // Hide backend SQL/ORM internals from end users.
    if (technicalPattern.test(message) || message.length > 220) {
      return fallback;
    }

    return message;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitInProgressRef.current) {
      return;
    }
    submitInProgressRef.current = true;

    const newErrors = computeValidationErrors();
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstKey = getFirstErrorScrollKey(newErrors);
      window.setTimeout(() => scrollToFirstErrorField(firstKey), 0);
      submitInProgressRef.current = false;
      return;
    }

    setSubmitting(true);
    try {
      // Validate form data before sending
      if (!formData.subject || !formData.description
          || !formData.inquiryTypeId || !formData.inquiryCategoryId
          || !formData.inquirySubCategoryId) {
        toast.error('Please fill in all required fields');
        setSubmitting(false);
        submitInProgressRef.current = false;
        return;
      }

      // Transform data to match backend DTO.
      // Inquiry Priority is set server-side to Not Prioritised — requester does not choose it.
      const ticketData = {
        subject: formData.subject.trim(),
        description: formData.description.trim(),
        inquiryTypeId: Number(formData.inquiryTypeId),
        inquiryCategoryId: Number(formData.inquiryCategoryId),
        inquirySubCategoryId: Number(formData.inquirySubCategoryId),
        source: source,
        channel: source === 'agent' ? intakeChannel : 'web',
        clientCreatedAt: (() => {
          const now = new Date();
          // Get timezone offset in minutes and convert to ±HH:MM format
          const offset = -now.getTimezoneOffset();
          const offsetHours = Math.floor(Math.abs(offset) / 60);
          const offsetMinutes = Math.abs(offset) % 60;
          const offsetSign = offset >= 0 ? '+' : '-';
          const offsetStr = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;

          // Format as ISO 8601 with timezone (e.g., "2026-02-23T06:16:00+05:30")
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          const hours = String(now.getHours()).padStart(2, '0');
          const minutes = String(now.getMinutes()).padStart(2, '0');
          const seconds = String(now.getSeconds()).padStart(2, '0');
          const milliseconds = String(now.getMilliseconds()).padStart(3, '0');

          return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${offsetStr}`;
        })()
      };

      // Add phone number if provided
      if (formData.phoneNumber && formData.phoneNumber.trim()) {
        ticketData.phoneNumber = formData.phoneNumber.trim();
      }

      if (isAgentOnBehalfCreate && selectedCustomer?.userId) {
        ticketData.requesterId = Number(selectedCustomer.userId);
      }

      // Add location data if provided
      if (locationData.countryId) {
        ticketData.countryId = locationData.countryId;
      }
      if (locationData.countyId) {
        ticketData.countyId = locationData.countyId;
      }
      if (locationData.subCountyId) {
        ticketData.subCountyId = locationData.subCountyId;
      }
      if (locationData.wardId) {
        ticketData.wardId = locationData.wardId;
      }

      // Include dynamic custom field values for template-based categories.
      if (templateFields.length > 0) {
        const normalizedCustomFieldValues = Object.entries(customFieldValues || {}).reduce((acc, [fieldCode, rawValue]) => {
          if (rawValue === null || rawValue === undefined) {
            return acc;
          }
          if (Array.isArray(rawValue)) {
            const cleaned = rawValue
              .map((item) => (item === null || item === undefined ? '' : String(item).trim()))
              .filter((item) => item.length > 0);
            if (cleaned.length > 0) {
              acc[fieldCode] = cleaned;
            }
            return acc;
          }
          if (typeof rawValue === 'string') {
            const trimmed = rawValue.trim();
            if (trimmed.length > 0) {
              acc[fieldCode] = trimmed;
            }
            return acc;
          }
          acc[fieldCode] = rawValue;
          return acc;
        }, {});

        ticketData.customFieldValues = normalizedCustomFieldValues;
      }

      // Log the data being sent for debugging
      //console.log('Sending ticket data:', ticketData);

      // Create ticket — branches on parent-child create-child mode (V1, agent only).
      // The createChildTicket endpoint internally orchestrates createTicket + relation
      // insert in one transaction, so attachments and post-create flow stay identical.
      let response;
      if (isCreateChildMode) {
        response = await ticketService.createChildTicket(parentTicketIdNum, ticketData);
        // The create-child endpoint returns { child: TicketResponse, relation: RelationResponse }
        // wrapped in ResponseBean. Normalize the shape so the rest of this handler can
        // continue treating `response.data.data` as a TicketResponse.
        const apiData = response?.data;
        if (apiData?.success && apiData?.data?.child) {
          response = { data: { ...apiData, data: apiData.data.child } };
        }
      } else {
        response = await ticketService.createTicket(ticketData);
      }

      // Backend returns ResponseBean: { status, message, data, success, timestamp }
      const responseData = response?.data;

      if (responseData?.success && responseData?.data) {
        const createdTicket = responseData.data;
        const ticketId = createdTicket.ticketId;

        // Handle attachments if any - Use file service API
        // If uploads fail, don't show success - ticket will be rolled back by backend
        if (formData.attachments.length > 0 && ticketId) {
          try {
            // Use Promise.all - fails if any upload fails
            await Promise.all(
              formData.attachments.map(async (file) => {
                try {
                  await fileService.uploadFile(file, {
                    reg_id: parseInt(ticketId),
                    fileTypeId: 1,
                    isActive: true,
                    screenName: 'TICKET'
                  });
                } catch (fileUploadError) {
                  // Attach file name to error for better reporting
                  fileUploadError.fileName = file.name;
                  throw fileUploadError; // Re-throw to be caught by outer Promise.all
                }
              })
            );

            // All uploads successful
            toast.success(`${formData.attachments.length} file(s) uploaded successfully`);
          } catch (uploadError) {
            console.error('Error uploading files:', uploadError);

            // Extract error message from API response (ResponseModel structure)
            let errorMessage = 'File upload failed. Please check file size and type.';

            // Helper function to extract clean message from error
            const extractApiMessage = (error) => {
              // Priority 1: Check if error has response.data (axios-style structure)
              if (error?.response?.data) {
                const data = error.response.data;
                // Try ResponseModel structure: { response: { response: "message" } }
                if (data.response?.response) {
                  return data.response.response;
                }
                // Try direct response field
                if (data.response && typeof data.response === 'string') {
                  return data.response;
                }
                // Try message field
                if (data.message) {
                  return data.message;
                }
                // Try error field
                if (data.error) {
                  return data.error;
                }
                // Try nested data.message
                if (data.data?.message) {
                  return data.data.message;
                }
                // Try nested data.error
                if (data.data?.error) {
                  return data.data.error;
                }
              }

              // Priority 2: Check error.message (apiClient throws Error with message)
              if (error?.message) {
                let msg = error.message;

                // Remove "API Error: XXX" prefix if present
                if (msg.startsWith('API Error:')) {
                  // Try to extract actual message after "API Error: XXX"
                  const parts = msg.split('API Error:');
                  if (parts.length > 1) {
                    const afterPrefix = parts.slice(1).join('API Error:').trim();
                    // If there's meaningful content after the prefix, use it
                    if (afterPrefix && afterPrefix.length > 0 && !afterPrefix.match(/^\d+$/)) {
                      return afterPrefix;
                    }
                  }
                  // If no meaningful message after prefix, return generic message
                  return 'File size exceeds maximum allowed limit. Please select a smaller file.';
                }

                // Check if message is a JSON string that can be parsed
                if (typeof msg === 'string' && (msg.trim().startsWith('{') || msg.trim().startsWith('['))) {
                  try {
                    const parsed = JSON.parse(msg);
                    return parsed.message || parsed.error || parsed.response?.response || msg;
                  } catch {
                    // Not valid JSON, continue
                  }
                }

                return msg;
              }

              return null;
            };

            const extractedMessage = extractApiMessage(uploadError);

            if (extractedMessage) {
              // Remove "API Error: XXX" prefix if it still exists
              errorMessage = extractedMessage.replace(/^API Error:\s*\d+\s*/i, '').trim();

              // If errorMessage is just a number (status code), use default
              if (errorMessage.match(/^\d+$/)) {
                errorMessage = 'File size exceeds maximum allowed limit. Please select a smaller file.';
              }

              // If we have a file name, include it
              if (uploadError?.fileName) {
                errorMessage = `${uploadError.fileName}: ${errorMessage}`;
              }
            } else {
              // Fallback to default message
              if (uploadError?.fileName) {
                errorMessage = `${uploadError.fileName}: ${errorMessage}`;
              }
            }

            // Show error - ticket creation failed due to file upload failure
            // Backend will rollback the ticket automatically
            toast.error(`Ticket creation failed: ${errorMessage}`, { duration: 8000 });
            setSubmitting(false);
            submitInProgressRef.current = false;

            // Don't show success screen - ticket was rolled back
            return; // Exit early - don't show success
          }
        }

        // Only set success if we reach here (no attachments or all uploads succeeded)
        setCreatedTicketId(ticketId);
        setCreatedTicketNumber(createdTicket.ticketNumber || null);
        const canonicalNumber =
          createdTicket.linkedDuplicateOfTicketNumber
          || (createdTicket.linkedDuplicateOfTicketId
            ? `#${createdTicket.linkedDuplicateOfTicketId}`
            : null);
        setLinkedDuplicateOfNumber(canonicalNumber);
        setShowSuccess(true);

        // Reset form for next submission
        setFormData({
          subject: '',
          inquiryTypeId: null,
          inquiryCategoryId: null,
          inquirySubCategoryId: null,
          inquiryPriorityLevelId: null,
          description: '',
          phoneNumber: '',
          attachments: []
        });
        setInquiryCategories([]);
        setInquirySubCategories([]);
        setLocationData({
          countryId: null,
          countyId: null,
          subCountyId: null,
          wardId: null
        });
        setErrors({});
        setOversizedFiles([]);
        setTemplateFields([]);
        setCustomFieldValues({});
        if (source === 'agent') {
          setIntakeChannel('web');
        }
      } else {
        toast.error(sanitizeTicketCreationError(responseData?.message));
      }
    } catch (error) {
      console.error('Submit error:', error);
      const rawErrorMessage = error?.response?.data?.message ||
        error?.message ||
        'An error occurred. Please try again.';
      toast.error(sanitizeTicketCreationError(rawErrorMessage));
    } finally {
      setSubmitting(false);
      submitInProgressRef.current = false;
    }
  };

  const handleCancel = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate(redirectPath);
    }
  };

  const handleGoToMyTickets = () => {
    navigate(redirectPath);
  };

  const handleSubmitNewTicket = () => {
    setShowSuccess(false);
    setCreatedTicketId(null);
    setCreatedTicketNumber(null);
    setLinkedDuplicateOfNumber(null);
    setOversizedFiles([]); // Clear oversized files when starting new ticket
    setTemplateFields([]);
    setCustomFieldValues({});
    // Form is already reset, just need to hide success screen
  };

  // Render custom field based on field type
  const renderCustomField = (field) => {
    const value = customFieldValues[field.fieldCode] || '';
    const errorKey = `custom_${field.fieldCode}`;
    const hasError = errors[errorKey];
    const baseClassName = `w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${hasError ? 'border-red-500' : 'border-gray-300'
      } bg-white text-gray-900`;

    switch (field.fieldType) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.fieldCode, e.target.value)}
            placeholder={`Enter ${field.fieldName.toLowerCase()}...`}
            className={baseClassName}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleCustomFieldChange(field.fieldCode, e.target.value)}
            placeholder={`Enter ${field.fieldName.toLowerCase()}...`}
            rows={4}
            className={`${baseClassName} resize-none`}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.fieldCode, e.target.value)}
            placeholder={`Enter ${field.fieldName.toLowerCase()}...`}
            className={baseClassName}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.fieldCode, e.target.value)}
            className={baseClassName}
          />
        );

      case 'select':
        try {
          const options = field.fieldOptions ? JSON.parse(field.fieldOptions) : [];
          return (
            <select
              value={value}
              onChange={(e) => handleCustomFieldChange(field.fieldCode, e.target.value)}
              className={baseClassName}
            >
              <option value="">Select {field.fieldName.toLowerCase()}...</option>
              {Array.isArray(options) && options.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
          );
        } catch (e) {
          console.error('Error parsing select options:', e);
          return (
            <input
              type="text"
              value={value}
              onChange={(e) => handleCustomFieldChange(field.fieldCode, e.target.value)}
              placeholder="Invalid options configuration"
              className={baseClassName}
              disabled
            />
          );
        }

      case 'checkbox':
        try {
          const options = field.fieldOptions ? JSON.parse(field.fieldOptions) : [];
          return (
            <div className="space-y-2">
              {Array.isArray(options) && options.map((option, index) => {
                const isChecked = Array.isArray(value) ? value.includes(option) : false;
                return (
                  <label key={index} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => {
                        const currentValue = Array.isArray(value) ? value : [];
                        const newValue = e.target.checked
                          ? [...currentValue, option]
                          : currentValue.filter(v => v !== option);
                        handleCustomFieldChange(field.fieldCode, newValue);
                      }}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{option}</span>
                  </label>
                );
              })}
            </div>
          );
        } catch (e) {
          console.error('Error parsing checkbox options:', e);
          return (
            <p className="text-sm text-red-600">Invalid checkbox options configuration</p>
          );
        }

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.fieldCode, e.target.value)}
            placeholder={`Enter ${field.fieldName.toLowerCase()}...`}
            className={baseClassName}
          />
        );
    }
  };

  if (loadingReferences) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading ticket form...</p>
        </div>
      </div>
    );
  }

  // Show success screen after successful submission
  if (showSuccess) {
    return (
      <div className="w-full p-6">
        <Card className="text-center">
          <CardContent className="pt-12 pb-12">
            <div className="flex flex-col items-center justify-center space-y-6">
              {/* Success Icon */}
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>

              {/* Success Message */}
              <div className="space-y-2">
                <h2 className="text-3xl font-bold text-gray-900">
                  Ticket Submitted Successfully!
                </h2>
                <p className="text-lg text-gray-600">
                  Your support ticket has been created and submitted successfully.
                </p>
                {(createdTicketNumber || createdTicketId) && (
                  <p className="text-sm text-gray-500 mt-2">
                    Ticket Number:{' '}
                    <span className="font-semibold">
                      {createdTicketNumber || `#${createdTicketId}`}
                    </span>
                  </p>
                )}
              </div>

              {linkedDuplicateOfNumber && (
                <div className="w-full max-w-lg rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-left">
                  <p className="text-sm text-blue-900">
                    This ticket was identified as a duplicate of an existing ticket (
                    <span className="font-mono font-medium">{linkedDuplicateOfNumber}</span>
                    ).
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-center gap-4 mt-8">
                <Button
                  onClick={handleGoToMyTickets}
                  className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3"
                >
                  <Ticket className="h-5 w-5" />
                  <span>Go to My Tickets</span>
                </Button>
                <Button
                  onClick={handleSubmitNewTicket}
                  variant="outline"
                  className="flex items-center space-x-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-6 py-3"
                >
                  <Plus className="h-5 w-5" />
                  <span>Submit New Ticket</span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full p-4">
      {/* {source === 'agent' && (
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {title}
          </h1>
          <p className="text-gray-600">
            {subtitle}
          </p>
        </div>
      )} */}

      {/* Parent-context banner — only renders when launched as "create child" from a
          parent ticket (agent path + feature flag on). Customer portal never sees this. */}
      {isCreateChildMode && (
        <div className="mb-6 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4">
          <div className="flex items-start gap-3">
            <GitBranch className="h-5 w-5 mt-0.5 text-blue-700 dark:text-blue-300 flex-shrink-0" />
            <div className="text-sm">
              <div className="font-medium text-blue-900 dark:text-blue-100">
                Creating a child ticket
              </div>
              <div className="mt-1 text-blue-800 dark:text-blue-200">
                This new ticket will be created and automatically linked under parent{' '}
                <span className="font-mono font-semibold">
                  {rawParentTicketNumber || `#${parentTicketIdNum}`}
                </span>
                . The two tickets remain fully independent — status, assignment, SLA and
                escalation are not propagated.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Ticket Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Portal: logged-in customer identity + phone above Subject */}
            {isPortalCreate && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={customerProfile.name}
                    readOnly
                    tabIndex={-1}
                    aria-readonly="true"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">
                   Email
                  </label>
                  <input
                    type="email"
                    value={customerProfile.email}
                    readOnly
                    tabIndex={-1}
                    aria-readonly="true"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">
                    <Phone className="inline h-4 w-4 mr-1" />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    placeholder="Enter your phone number (optional)..."
                    maxLength={10}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.phoneNumber ? 'border-red-500' : 'border-gray-300'
                    } bg-white text-gray-900`}
                  />
                  {errors.phoneNumber && (
                    <p className="text-sm text-red-600 mt-1">{errors.phoneNumber}</p>
                  )}
                </div>
              </div>
            )}

            {/* Agent: create on behalf of customer — above Subject */}
            {isAgentOnBehalfCreate && (
              <div
                ref={(el) => { fieldScrollRefs.current.customerRequester = el; }}
                className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4"
              >
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-700" />
                  <h3 className="text-sm font-semibold text-gray-900">
                    Create Ticket On Behalf Of
                  </h3>
                </div>

                <p className="text-sm text-gray-600">
                  Search for the customer by email, then complete the ticket details.
                </p>

                <div>
                  <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] gap-x-2 gap-y-2 sm:items-end">
                    <div className="min-w-0">
                      <label className="block text-sm font-medium text-gray-900 mb-1">
                        Customer Email <span className="text-red-500 font-semibold" aria-hidden="true">*</span>
                      </label>
                      <input
                        type="email"
                        value={customerEmailSearch}
                        onChange={(e) => handleCustomerEmailChange(e.target.value)}
                        onKeyDown={handleCustomerSearchKeyDown}
                        placeholder="Search customer by email..."
                        disabled={customerSearchLoading}
                        readOnly={Boolean(selectedCustomer)}
                        aria-readonly={selectedCustomer ? 'true' : undefined}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.customerRequester || customerSearchError
                            ? 'border-red-500'
                            : 'border-gray-300'
                        } ${selectedCustomer ? 'bg-gray-100 text-gray-700 cursor-not-allowed' : 'bg-white text-gray-900'}`}
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={handleSearchCustomer}
                      disabled={customerSearchLoading || !customerEmailSearch.trim() || Boolean(selectedCustomer)}
                      className="sm:w-auto w-full sm:self-end"
                    >
                      {customerSearchLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Searching...
                        </>
                      ) : (
                        <>
                          <Search className="h-4 w-4 mr-2" />
                          Search
                        </>
                      )}
                    </Button>
                  </div>
                  {(customerSearchError || errors.customerRequester) && (
                    <p className="text-sm text-red-600 mt-1">
                      {customerSearchError || errors.customerRequester}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">
                    Intake channel
                  </label>
                  <p className="text-xs text-gray-500 mb-1">
                    How the customer contacted the help desk.
                  </p>
                  <select
                    value={intakeChannel}
                    onChange={(e) => setIntakeChannel(e.target.value)}
                    disabled={submitting}
                    className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    {HDO_INTAKE_CHANNELS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedCustomer && (
                  <div className="rounded-lg border border-green-200 bg-white p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-gray-900">Customer</p>
                      <button
                        type="button"
                        onClick={handleChangeCustomer}
                        className="text-sm font-medium text-blue-700 hover:text-blue-900 underline"
                      >
                        Change Customer
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                      <p><span className="font-medium text-gray-900">Full Name:</span> {selectedCustomer.fullName || '—'}</p>
                      <p><span className="font-medium text-gray-900">Email:</span> {selectedCustomer.email || '—'}</p>
                      <p className="md:col-span-2">
                        <span className="font-medium text-gray-900">Channel:</span>{' '}
                        {getIntakeChannelLabel(intakeChannel)}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">
                        <Phone className="inline h-4 w-4 mr-1" />
                        Phone (optional, for this ticket)
                      </label>
                      <input
                        type="tel"
                        value={formData.phoneNumber}
                        onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                        placeholder="Enter phone number for this ticket..."
                        maxLength={10}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className={`w-full max-w-xs px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.phoneNumber ? 'border-red-500' : 'border-gray-300'
                        } bg-white text-gray-900`}
                      />
                      {errors.phoneNumber && (
                        <p className="text-sm text-red-600 mt-1">{errors.phoneNumber}</p>
                      )}
                    </div>
                  </div>
                )}

                {agentFormLocked && (
                  <p className="text-sm text-gray-500 italic">
                    Select a customer to enable the rest of the form.
                  </p>
                )}
              </div>
            )}

            {/* Subject */}
            <div ref={(el) => { fieldScrollRefs.current.subject = el; }}>
              <label className="block text-sm font-medium text-gray-900 mb-1">
                Subject <span className="text-red-500 dark:text-red-500 font-semibold" aria-hidden="true">*</span>
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                placeholder="Enter a brief description of your issue..."
                disabled={agentFormLocked}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.subject
                  ? 'border-red-500'
                  : 'border-gray-300'
                  } ${agentFormLocked ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white text-gray-900'}`}
              />
              {errors.subject && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.subject}
                </p>
              )}
            </div>

            {/* Agent: Inquiry Type (+ phone only when no customer selected yet) */}
            {!isPortalCreate && (
              <div className={`grid grid-cols-1 gap-3 ${selectedCustomer ? 'md:grid-cols-1' : 'md:grid-cols-2'}`}>
                <div ref={(el) => { fieldScrollRefs.current.inquiryType = el; }}>
                  <label className="block text-sm font-medium text-gray-900 mb-1">
                    Inquiry Type <span className="text-red-500 dark:text-red-500 font-semibold" aria-hidden="true">*</span>
                  </label>
                  <select
                    value={formData.inquiryTypeId || ''}
                    onChange={(e) => handleInputChange('inquiryTypeId', e.target.value ? Number(e.target.value) : null)}
                    disabled={loadingReferences || agentFormLocked}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.inquiryType ? 'border-red-500' : 'border-gray-300'
                    } bg-white text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                  >
                    <option value="">Select inquiry type...</option>
                    {inquiryTypes.map((type) => (
                      <option key={type.id} value={type.id}>{type.inquiryTypeName}</option>
                    ))}
                  </select>
                  {errors.inquiryType && (
                    <p className="text-sm text-red-600 mt-1">{errors.inquiryType}</p>
                  )}
                </div>
                {!selectedCustomer && (
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1">
                      <Phone className="inline h-4 w-4 mr-1" />
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                      placeholder="Enter your phone number (optional)..."
                      maxLength={10}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      disabled={agentFormLocked}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.phoneNumber ? 'border-red-500' : 'border-gray-300'
                      } ${agentFormLocked ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white text-gray-900'}`}
                    />
                    {errors.phoneNumber && (
                      <p className="text-sm text-red-600 mt-1">{errors.phoneNumber}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Inquiry Type / Category / Sub Category — one row on portal */}
            <div className={`grid grid-cols-1 gap-3 ${isPortalCreate ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
              {isPortalCreate && (
                <div ref={(el) => { fieldScrollRefs.current.inquiryType = el; }}>
                  <label className="block text-sm font-medium text-gray-900 mb-1">
                    Inquiry Type <span className="text-red-500 dark:text-red-500 font-semibold" aria-hidden="true">*</span>
                  </label>
                  <select
                    value={formData.inquiryTypeId || ''}
                    onChange={(e) => handleInputChange('inquiryTypeId', e.target.value ? Number(e.target.value) : null)}
                    disabled={loadingReferences}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.inquiryType ? 'border-red-500' : 'border-gray-300'
                    } bg-white text-gray-900`}
                  >
                    <option value="">Select inquiry type...</option>
                    {inquiryTypes.map((type) => (
                      <option key={type.id} value={type.id}>{type.inquiryTypeName}</option>
                    ))}
                  </select>
                  {errors.inquiryType && (
                    <p className="text-sm text-red-600 mt-1">{errors.inquiryType}</p>
                  )}
                </div>
              )}

              <div ref={(el) => { fieldScrollRefs.current.inquiryCategory = el; }}>
                <label className="block text-sm font-medium text-gray-900 mb-1">
                  Inquiry Category <span className="text-red-500 dark:text-red-500 font-semibold" aria-hidden="true">*</span>
                </label>
                <select
                  value={formData.inquiryCategoryId || ''}
                  onChange={(e) => handleInputChange('inquiryCategoryId', e.target.value ? Number(e.target.value) : null)}
                  disabled={!formData.inquiryTypeId || agentFormLocked}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.inquiryCategory ? 'border-red-500' : 'border-gray-300'
                  } bg-white text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                >
                  <option value="">
                    {formData.inquiryTypeId ? 'Select inquiry category...' : 'Select inquiry type first'}
                  </option>
                  {inquiryCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.inquiryCategoryName}</option>
                  ))}
                </select>
                {errors.inquiryCategory && (
                  <p className="text-sm text-red-600 mt-1">{errors.inquiryCategory}</p>
                )}
              </div>

              <div ref={(el) => { fieldScrollRefs.current.inquirySubCategory = el; }}>
                <label className="block text-sm font-medium text-gray-900 mb-1">
                  Inquiry Sub Category <span className="text-red-500 dark:text-red-500 font-semibold" aria-hidden="true">*</span>
                </label>
                <select
                  value={formData.inquirySubCategoryId || ''}
                  onChange={(e) => handleInputChange('inquirySubCategoryId', e.target.value ? Number(e.target.value) : null)}
                  disabled={!formData.inquiryCategoryId || agentFormLocked}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.inquirySubCategory ? 'border-red-500' : 'border-gray-300'
                  } bg-white text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                >
                  <option value="">
                    {formData.inquiryCategoryId ? 'Select inquiry sub category...' : 'Select inquiry category first'}
                  </option>
                  {inquirySubCategories.map((sub) => (
                    <option key={sub.id} value={sub.id}>{sub.inquirySubCategoryName}</option>
                  ))}
                </select>
                {errors.inquirySubCategory && (
                  <p className="text-sm text-red-600 mt-1">{errors.inquirySubCategory}</p>
                )}
              </div>
            </div>

            {/* Location Selector — Country hidden on portal (Kenya remains the default) */}
            <div className={agentFormLocked ? 'pointer-events-none opacity-60' : ''}>
              <label className="block text-sm font-medium text-gray-900 mb-1">
                Location (Optional)
              </label>
              <LocationSelector
                onLocationChange={(location) => {
                  setLocationData({
                    countryId: location.countryId,
                    countyId: location.countyId,
                    subCountyId: location.subCountyId,
                    wardId: location.wardId
                  });
                }}
                showAll={false}
                horizontal={true}
                hideCountry={isPortalCreate}
              />
            </div>

            {/* Description */}
            <div ref={(el) => { fieldScrollRefs.current.description = el; }}>
              <label className="block text-sm font-medium text-gray-900 mb-1">
                Description <span className="text-red-500 dark:text-red-500 font-semibold" aria-hidden="true">*</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Please provide detailed information about your issue..."
                rows={3}
                disabled={agentFormLocked}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.description
                  ? 'border-red-500'
                  : 'border-gray-300'
                  } ${agentFormLocked ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white text-gray-900'} resize-none`}
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.description.length}/5000 characters
              </p>
              {errors.description && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.description}
                </p>
              )}
            </div>

            {/* File Attachments */}
            <div ref={(el) => { fieldScrollRefs.current.attachments = el; }} className={agentFormLocked ? 'pointer-events-none opacity-60' : ''}>
              <label className="block text-sm font-medium text-gray-900 mb-1">
                Attachments
              </label>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
                accept="*/*"
              />
              <label
                htmlFor="file-upload"
                className={`flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-dashed px-4 py-2.5 transition-colors ${
                  errors.attachments
                  ? 'border-red-300 bg-red-50'
                  : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50/60'
                  }`}
              >
                <span className="flex min-w-0 items-center gap-3">
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                    errors.attachments
                    ? 'bg-red-100 text-red-500'
                    : 'border border-gray-200 bg-white text-blue-500'
                    }`}>
                    <Upload className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className={`block truncate text-sm font-medium ${
                      errors.attachments ? 'text-red-700' : 'text-gray-700'
                      }`}>
                      Drag and drop files here, or click to select
                    </span>
                    <span className={`block truncate text-xs ${
                      errors.attachments ? 'font-semibold text-red-600' : 'text-gray-500'
                      }`}>
                      {errors.attachments
                        ? `⚠️ ${errors.attachments}`
                        : 'All file types supported · Maximum 5MB per file'}
                    </span>
                  </span>
                </span>
                <span className="inline-flex shrink-0 items-center rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50">
                  Choose Files
                </span>
              </label>

              {/* Valid Attachments */}
              {formData.attachments.length > 0 && (
                <div className="mt-2 space-y-2">
                  <p className="text-xs font-medium text-gray-700">Valid Files ({formData.attachments.length}):</p>
                  {formData.attachments.map((file, index) => (
                    <div key={index} className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-3 py-2">
                      <div className="flex min-w-0 items-center space-x-3">
                        <FileText className="h-4 w-4 shrink-0 text-green-600" />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-gray-900">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-600">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                        aria-label="Remove file"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Oversized Files (Rejected) */}
              {oversizedFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-xs font-medium text-red-700 mb-2 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    Files Exceeding Size Limit ({oversizedFiles.length}) - Remove these before submitting:
                  </p>
                  {oversizedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border-2 border-red-300">
                      <div className="flex items-center space-x-3 flex-1">
                        <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-red-900 truncate">
                            {file.name}
                          </p>
                          <p className="text-xs text-red-700">
                            {formatFileSize(file.size)} - Exceeds 5MB limit
                          </p>
                          <p className="text-xs text-red-600 mt-1 font-semibold">
                            ⚠️ This file must be removed before you can submit the ticket
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeOversizedFile(index)}
                        className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors flex-shrink-0 ml-2"
                        aria-label="Remove oversized file"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end ">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={submitting}
                className="flex items-center space-x-2"
              >
                <X size={18} />
                <span>Cancel</span>
              </Button>
              <Button
                type="submit"
                disabled={submitting || loadingReferences || oversizedFiles.length > 0 || agentFormLocked}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed ml-2"
                title={oversizedFiles.length > 0 ? 'Please remove oversized files before submitting' : ''}
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <span>Submit Ticket</span>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default CreateTicketForm;
